/**
 * Error handling and exit codes.
 *
 * Exit codes:
 * 0 - Success
 * 1 - General error (network, unexpected API error)
 * 2 - Authentication error
 * 3 - Not found
 * 4 - Validation error (missing flag, invalid input)
 * 5 - Conflict (resource state prevents the operation)
 */

import { ORPCError } from '@orpc/client'
import { AuthError } from '../auth.js'

export const EXIT_SUCCESS = 0
export const EXIT_GENERAL_ERROR = 1
export const EXIT_AUTH_ERROR = 2
export const EXIT_NOT_FOUND = 3
export const EXIT_VALIDATION_ERROR = 4
export const EXIT_CONFLICT = 5

function errorNameForCode(code: number): string {
  if (code === EXIT_AUTH_ERROR) return `AUTH_ERROR`
  if (code === EXIT_NOT_FOUND) return `NOT_FOUND`
  if (code === EXIT_VALIDATION_ERROR) return `VALIDATION_ERROR`
  if (code === EXIT_CONFLICT) return `CONFLICT`
  return `ERROR`
}

export function exitWithError(
  message: string,
  code: number,
  json: boolean
): never {
  if (json) {
    process.stderr.write(
      JSON.stringify({
        error: errorNameForCode(code),
        message,
        exitCode: code,
      }) + `\n`
    )
  } else {
    process.stderr.write(`Error: ${message}\n`)
  }
  process.exit(code)
}

export function handleError(error: unknown, json: boolean): never {
  const { code, message } = classifyError(error)
  exitWithError(message, code, json)
}

function classifyByStatus(status: number): number {
  if (status === 401 || status === 403) return EXIT_AUTH_ERROR
  if (status === 404) return EXIT_NOT_FOUND
  if (status === 400 || status === 422) return EXIT_VALIDATION_ERROR
  return EXIT_GENERAL_ERROR
}

export function classifyError(error: unknown): {
  code: number
  message: string
} {
  if (error instanceof AuthError) {
    return { code: EXIT_AUTH_ERROR, message: error.message }
  }

  // Use structured oRPC error codes when available
  if (error instanceof ORPCError) {
    return {
      code: classifyByStatus(error.status),
      message: error.message,
    }
  }

  if (error instanceof Error) {
    return { code: EXIT_GENERAL_ERROR, message: error.message }
  }

  return { code: EXIT_GENERAL_ERROR, message: String(error) }
}
